import { useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';
import { api } from '../api/client';
import SectionToggle from '../components/SectionToggle';
import PageSettings from '../components/PageSettings';
import DraggableList from '../components/DraggableList';
import '../styles/editor.css';

interface BulkEdits {
  [contentKey: string]: {
    price?: string;
    category?: string;
    dietaryTags?: string;
    available?: string;
  };
}

export default function MenuList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, remove, reorder, refresh } = useContent(siteId, 'menu');
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEdits, setBulkEdits] = useState<BulkEdits>({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });

  // Build category list with item counts
  const categoryOptions = useMemo(() => {
    const countMap: Record<string, number> = {};
    items.forEach((item) => {
      const cat = (item.data as Record<string, string>).category;
      if (cat) {
        countMap[cat] = (countMap[cat] || 0) + 1;
      }
    });
    const names = Object.keys(countMap).sort();
    return names.map((name) => ({
      name,
      count: countMap[name] || 0,
    }));
  }, [items]);

  // Filtered items
  const displayedItems = useMemo(() => {
    if (!filterCategory) return items;
    return items.filter((item) => (item.data as Record<string, string>).category === filterCategory);
  }, [items, filterCategory]);

  // Count of changed items
  const changedCount = useMemo(() => Object.keys(bulkEdits).length, [bulkEdits]);

  const handleDelete = async (contentKey: string, title: string) => {
    if (!confirm(`Delete "${title}"?`)) return;
    try {
      const id = getContentId(contentKey);
      await remove(id);
      addToast('success', 'Menu item deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    const newItems = [...items];
    const [moved] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, moved);
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  const handleBulkFieldChange = useCallback(
    (contentKey: string, field: string, value: string) => {
      setBulkEdits((prev) => {
        const existing = prev[contentKey] || {};
        const updated = { ...existing, [field]: value };

        // Get original data for this item to check if we actually changed anything
        const item = items.find((i) => i.contentKey === contentKey);
        if (!item) return prev;
        const originalData = item.data as Record<string, string>;

        // Check each field in the updated edits — remove if same as original
        const cleaned: Record<string, string> = {};
        for (const [k, v] of Object.entries(updated)) {
          const orig = originalData[k] ?? '';
          if (v !== orig) {
            cleaned[k] = v;
          }
        }

        const next = { ...prev };
        if (Object.keys(cleaned).length === 0) {
          delete next[contentKey];
        } else {
          next[contentKey] = cleaned;
        }
        return next;
      });
    },
    [items],
  );

  const handleToggleBulkEdit = () => {
    if (bulkEditMode) {
      // Exiting edit mode — discard changes
      setBulkEdits({});
      setBulkEditMode(false);
    } else {
      setBulkEditMode(true);
    }
  };

  const handleCancelBulkEdit = () => {
    setBulkEdits({});
  };

  const handleSaveBulkEdits = async () => {
    if (!siteId) return;
    const entries = Object.entries(bulkEdits);
    if (entries.length === 0) return;

    setSaving(true);
    setSaveProgress({ current: 0, total: entries.length });
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < entries.length; i++) {
      const [contentKey, changes] = entries[i];
      const item = items.find((it) => it.contentKey === contentKey);
      if (!item) continue;

      const contentId = getContentId(contentKey);
      const mergedData = { ...(item.data as Record<string, unknown>), ...changes };

      setSaveProgress({ current: i + 1, total: entries.length });

      try {
        await api.updateContent(siteId, 'menu', contentId, mergedData);
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setSaving(false);
    setBulkEdits({});
    setBulkEditMode(false);
    await refresh();

    if (errorCount > 0) {
      addToast('error', `Saved ${successCount} items, ${errorCount} failed`);
    } else {
      addToast('success', `Saved ${successCount} item${successCount === 1 ? '' : 's'}`);
    }
  };

  const getEditValue = (contentKey: string, field: string, originalValue: string): string => {
    const edits = bulkEdits[contentKey];
    if (edits && field in edits) {
      return (edits as Record<string, string>)[field];
    }
    return originalValue;
  };

  // Unique category list for datalist
  const allCategoryNames = useMemo(() => {
    const names = new Set<string>();
    items.forEach((item) => {
      const cat = (item.data as Record<string, string>).category;
      if (cat) names.add(cat);
    });
    return Array.from(names).sort();
  }, [items]);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Menu</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Menu</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Menu</h1>
        <div className="page-header-actions">
          <SectionToggle siteId={siteId!} field="showMenu" label="Menu" />
          {items.length > 0 && (
            <button
              className={`btn-edit-all${bulkEditMode ? ' active' : ''}`}
              onClick={handleToggleBulkEdit}
              disabled={saving}
            >
              {bulkEditMode ? '✕ Done Editing' : '✏️ Edit All'}
            </button>
          )}
          <Link to={`/sites/${siteId}/content/menu/new`} className="btn btn-primary">
            + Add Item
          </Link>
        </div>
      </div>

      <PageSettings siteId={siteId!} fields={['menuTitle', 'menuSubtitle']} />

      {categoryOptions.length >= 2 && (
        <div className="list-filter-bar">
          <select
            className="field-input field-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ maxWidth: 260 }}
          >
            <option value="">All Categories</option>
            {categoryOptions.map((cat) => (
              <option key={cat.name} value={cat.name}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Datalist for category suggestions in bulk edit */}
      {bulkEditMode && (
        <datalist id="menu-category-suggestions">
          {allCategoryNames.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      )}

      {displayedItems.length === 0 && filterCategory ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
          <h2>No items in &ldquo;{filterCategory}&rdquo;</h2>
          <p>Try selecting a different category or clear the filter.</p>
          <button className="btn btn-primary" onClick={() => setFilterCategory('')}>
            Show All Items
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg></div>
          <h2>No menu items yet</h2>
          <p>Add dishes and drinks to build your menu.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: 16 }}>
            Menu items are grouped by category on your site.
          </p>
          <Link to={`/sites/${siteId}/content/menu/new`} className="btn btn-primary">
            + Add your first item
          </Link>
        </div>
      ) : (
        <DraggableList
          items={displayedItems.map((item) => {
            const data = item.data as Record<string, string>;
            const contentId = getContentId(item.contentKey);
            const isChanged = !!bulkEdits[item.contentKey];
            const itemClasses = [
              bulkEditMode ? 'bulk-edit-mode' : '',
              isChanged ? 'bulk-edit-changed' : '',
            ].filter(Boolean).join(' ');

            // Determine available value — treat missing/undefined as "true" (available by default)
            const rawAvailable = data.available;
            const isAvailable = rawAvailable === 'false' || rawAvailable === (false as unknown as string) ? 'false' : 'true';

            return {
              key: item.contentKey,
              className: itemClasses,
              draggable: !bulkEditMode,
              element: (
                <>
                  <div
                    className="list-item-content"
                    onClick={() => {
                      if (!bulkEditMode) {
                        navigate(`/sites/${siteId}/content/menu/${contentId}`);
                      }
                    }}
                    style={bulkEditMode ? { cursor: 'default' } : undefined}
                  >
                    <h3 className="list-item-title">
                      {data.name || 'Untitled'}
                      {!bulkEditMode && (data.available === 'false' || data.available === (false as unknown as string))
                        ? <span style={{ color: '#999', fontSize: '0.8em', marginLeft: '0.5rem' }}>(unavailable)</span>
                        : null}
                    </h3>
                    {bulkEditMode ? (
                      <div className="bulk-edit-fields">
                        <div className="bulk-edit-field">
                          <span className="bulk-edit-label">Price</span>
                          <input
                            type="text"
                            className="bulk-edit-input"
                            value={getEditValue(item.contentKey, 'price', data.price || '')}
                            onChange={(e) =>
                              handleBulkFieldChange(item.contentKey, 'price', e.target.value)
                            }
                            placeholder="e.g. $9.99"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="bulk-edit-field">
                          <span className="bulk-edit-label">Category</span>
                          <input
                            type="text"
                            className="bulk-edit-input"
                            list="menu-category-suggestions"
                            value={getEditValue(item.contentKey, 'category', data.category || '')}
                            onChange={(e) =>
                              handleBulkFieldChange(item.contentKey, 'category', e.target.value)
                            }
                            placeholder="Category"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="bulk-edit-field">
                          <span className="bulk-edit-label">Dietary Tags</span>
                          <input
                            type="text"
                            className="bulk-edit-input"
                            value={getEditValue(item.contentKey, 'dietaryTags', data.dietaryTags || '')}
                            onChange={(e) =>
                              handleBulkFieldChange(item.contentKey, 'dietaryTags', e.target.value)
                            }
                            placeholder="e.g. Vegan, GF"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: 140 }}
                          />
                        </div>
                        <div className="bulk-edit-field">
                          <label className="bulk-edit-checkbox" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={getEditValue(item.contentKey, 'available', isAvailable) !== 'false'}
                              onChange={(e) =>
                                handleBulkFieldChange(
                                  item.contentKey,
                                  'available',
                                  e.target.checked ? 'true' : 'false',
                                )
                              }
                            />
                            <span className="bulk-edit-label">Available</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="list-item-meta">
                        {data.category && <span className="tag">{data.category}</span>}
                        {data.price && <span className="list-item-price">{data.price}</span>}
                        {data.dietaryTags && <span className="tag" style={{ fontSize: '0.75rem' }}>{data.dietaryTags}</span>}
                      </div>
                    )}
                  </div>
                  {!bulkEditMode && (
                    <div className="list-item-actions">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(item.contentKey, data.name || 'this item')}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              ),
            };
          })}
          onReorder={handleReorder}
          disabled={bulkEditMode}
        />
      )}

      {/* Floating save bar */}
      {bulkEditMode && changedCount > 0 && (
        <div className="bulk-save-bar">
          {saving ? (
            <span className="bulk-save-bar-text">
              Saving {saveProgress.current} of {saveProgress.total}…
            </span>
          ) : (
            <>
              <span className="bulk-save-bar-text">
                {changedCount} unsaved change{changedCount === 1 ? '' : 's'}
              </span>
              <button className="btn btn-primary" onClick={handleSaveBulkEdits}>
                Save {changedCount} change{changedCount === 1 ? '' : 's'}
              </button>
              <button className="btn btn-cancel" onClick={handleCancelBulkEdit}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
